// Arquivo: src/components/ChatWidget.js

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useChat } from '../hooks/useChat';
import api from '../services/api';
import './ChatWidget.css';
import {
    FaPaperPlane, FaPaperclip, FaTimes, FaCommentDots,
    FaSignOutAlt, FaSmile, FaTasks, FaWrench,
    FaArrowLeft, FaStar, FaRegStar, FaSearch, FaSpinner,
    FaFileAlt, FaExclamationTriangle
} from 'react-icons/fa';
import Picker from 'emoji-picker-react';
import LinkTaskModal from './LinkTaskModal';
import { Link } from 'react-router-dom';

// ─────────────────────────────────────────────
// Sub-componentes
// ─────────────────────────────────────────────

const UserAvatar = ({ user, size = 'normal', isOnline = false }) => {
    const getInitials = (name = '') => {
        if (!name || name === 'Desconhecido') return '?';
        const parts = name.trim().split(' ');
        return parts.length > 1
            ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
            : name.substring(0, 2).toUpperCase();
    };

    const avatar = user?.avatar || user?.foto_perfil;
    const hasValidAvatar =
        avatar && typeof avatar === 'string' &&
        (avatar.startsWith('data:image') || avatar.startsWith('http'));

    return (
        <div className="avatar-wrapper">
            {hasValidAvatar ? (
                <img
                    src={avatar}
                    alt={user?.name || user?.nome || ''}
                    className={`user-avatar-img ${size}`}
                    onError={(e) => { e.target.style.display = 'none'; }}
                />
            ) : (
                <div className={`avatar-placeholder ${size}`}>
                    {getInitials(user?.name || user?.nome)}
                </div>
            )}
            {isOnline && <span className={`online-status-badge ${size}`} title="Online" />}
        </div>
    );
};

const TypingIndicator = () => (
    <div className="typing-indicator" aria-label="Digitando">
        <div className="typing-dot" /><div className="typing-dot" /><div className="typing-dot" />
    </div>
);

const LinkCard = ({ data }) => {
    const to = data.tipo === 'Tarefa'
        ? '/quadro-tarefas'
        : `/ordens/${data.id.replace('os-', '')}`;
    return (
        <Link to={to} className="link-card" onClick={e => e.stopPropagation()}>
            {data.tipo === 'Tarefa'
                ? <FaTasks className="link-card-icon task" />
                : <FaWrench className="link-card-icon os" />}
            <div className="link-card-info">
                <span className="link-card-title">{data.titulo}</span>
                <span className="link-card-type">{data.tipo}</span>
            </div>
        </Link>
    );
};

// Anexo de chat armazenado no Google Drive (privado).
// Busca o arquivo pela rota autenticada (blob) — um <a href> simples não
// carrega o token JWT, então imagem/preview precisam vir por api + objectURL.
const ChatAttachment = ({ file }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [status,  setStatus]  = useState('loading'); // loading | ready | error

    const fileName = file?.fileName || 'arquivo';
    const isImage  = String(file?.mimeType || '').startsWith('image/');

    useEffect(() => {
        let cancelled = false;
        let createdUrl = null;

        const fetchBlob = async () => {
            if (!file?.driveFileId) { setStatus('error'); return; }
            try {
                const { data } = await api.get(`/api/anexos/drive/${file.driveFileId}`, {
                    responseType: 'blob',
                });
                if (cancelled) return;
                createdUrl = URL.createObjectURL(data);
                setBlobUrl(createdUrl);
                setStatus('ready');
            } catch (err) {
                if (!cancelled) setStatus('error');
            }
        };

        fetchBlob();
        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [file?.driveFileId]);

    if (status === 'loading') {
        return (
            <div className="chat-attachment loading">
                <FaSpinner className="chat-attachment-spin" />
                <span className="chat-attachment-name">{fileName}</span>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="chat-attachment error">
                <FaExclamationTriangle />
                <span className="chat-attachment-name">Falha ao carregar {fileName}</span>
            </div>
        );
    }

    if (isImage) {
        return (
            <a
                href={blobUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="chat-attachment image"
                title={fileName}
            >
                <img src={blobUrl} alt={fileName} className="chat-attachment-img" />
            </a>
        );
    }

    return (
        <a
            href={blobUrl}
            download={fileName}
            target="_blank"
            rel="noopener noreferrer"
            className="chat-attachment file"
        >
            <FaFileAlt className="chat-attachment-icon" />
            <span className="chat-attachment-name">{fileName}</span>
        </a>
    );
};

const MessageBubble = ({ msg, activeChatUser }) => {
    const isSent = msg.type === 'sent';

    const renderContent = () => {
        if (msg.file?.type === 'link') return <LinkCard data={msg.file.linkData} />;
        // Anexo novo (Google Drive)
        if (msg.file?.kind === 'drive') return <ChatAttachment file={msg.file} />;
        // Anexo legado (base64 antigo em file_data) — mantém compatibilidade
        if (msg.file) {
            return (
                <a
                    href={msg.file.data}
                    download={msg.file.name}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="attachment-link"
                >
                    <FaPaperclip /> {msg.file.name}
                </a>
            );
        }
        return msg.text;
    };

    return (
        <div className={`message-bubble-wrapper ${msg.type}`}>
            <div className="message-bubble">{renderContent()}</div>
            {isSent && msg.status === 'read' && (
                <div className="read-receipt-avatar" title="Visto">
                    <UserAvatar user={activeChatUser} size="small" />
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────

export default function ChatWidget({ user }) {
    const [isOpen,           setIsOpen]           = useState(false);
    const [activeChatUserId, setActiveChatUserId]  = useState(null);
    const [messageText,      setMessageText]       = useState('');
    const [searchQuery,      setSearchQuery]       = useState('');
    const [showEmojiPicker,  setShowEmojiPicker]   = useState(false);
    const [showLinkModal,    setShowLinkModal]      = useState(false);
    const [uploadingFile,    setUploadingFile]      = useState(false);

    const {
        onlineUsers, allContacts, messages, sendMessage, sendFileMessage,
        typingUsers, startTyping, stopTyping,
        markMessagesAsRead, unreadSenders, toggleFavorite
    } = useChat(user, activeChatUserId, isOpen);

    const fileInputRef    = useRef(null);
    const messagesEndRef  = useRef(null);
    const messageInputRef = useRef(null);
    const typingTimeout   = useRef(null);

    // Fecha o widget e reseta estado
    const handleClose = useCallback(() => {
        setIsOpen(false);
        setActiveChatUserId(null);
        setShowEmojiPicker(false);
        setSearchQuery('');
    }, []);

    // Abre uma conversa
    const openConversation = useCallback((userId) => {
        setActiveChatUserId(userId);
        setSearchQuery('');
        markMessagesAsRead({ conversationPartnerId: userId });
    }, [markMessagesAsRead]);

    // Lista de contatos processada com status online e ordenação
    const usersToDisplay = useMemo(() => {
        return allContacts
            .filter(u => {
                const id = u.userId ?? u.id;
                if (id === user.id) return false;
                if (!searchQuery.trim()) return true;
                return (u.name || '').toLowerCase().includes(searchQuery.toLowerCase());
            })
            .map(u => ({
                ...u,
                userId:   u.userId ?? u.id,
                isOnline: onlineUsers.some(on => on.userId === (u.userId ?? u.id))
            }))
            .sort((a, b) =>
                (b.isFavorite - a.isFavorite) ||
                (b.isOnline   - a.isOnline)   ||
                (a.name || '').localeCompare(b.name || '')
            );
    }, [allContacts, onlineUsers, user.id, searchQuery]);

    // Scroll automático para última mensagem
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, activeChatUserId, typingUsers]);

    // Foca no input ao abrir uma conversa
    useEffect(() => {
        if (activeChatUserId && isOpen) {
            setTimeout(() => messageInputRef.current?.focus(), 100);
        }
    }, [activeChatUserId, isOpen]);

    // Fecha emoji picker ao clicar fora
    useEffect(() => {
        if (!showEmojiPicker) return;
        const handler = (e) => {
            if (!e.target.closest('.emoji-picker-wrapper') && !e.target.closest('.emoji-btn')) {
                setShowEmojiPicker(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showEmojiPicker]);

    const handleSend = useCallback((e) => {
        e.preventDefault();
        if (!messageText.trim() || !activeChatUserId) return;
        sendMessage({ senderId: user.id, receiverId: activeChatUserId, text: messageText });
        setMessageText('');
        setShowEmojiPicker(false);
        stopTyping(activeChatUserId);
    }, [messageText, activeChatUserId, sendMessage, stopTyping, user.id]);

    const handleTyping = useCallback((e) => {
        setMessageText(e.target.value);
        startTyping(activeChatUserId);
        if (typingTimeout.current) clearTimeout(typingTimeout.current);
        typingTimeout.current = setTimeout(() => stopTyping(activeChatUserId), 2000);
    }, [activeChatUserId, startTyping, stopTyping]);

    // Upload de anexo: sobe pela rota /messages/anexo (Drive) via sendFileMessage.
    const handleFileUpload = useCallback(async (file) => {
        if (!file || !activeChatUserId || !user?.id) return;
        setUploadingFile(true);
        try {
            await sendFileMessage({ senderId: user.id, receiverId: activeChatUserId, file });
        } finally {
            setUploadingFile(false);
        }
    }, [activeChatUserId, sendFileMessage, user?.id]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            if (activeChatUserId) setActiveChatUserId(null);
            else handleClose();
        }
    }, [activeChatUserId, handleClose]);

    const activeChatUser  = allContacts.find(u => (u.userId ?? u.id) === activeChatUserId);
    const isTyping        = typingUsers[activeChatUserId];
    const totalUnread     = unreadSenders.size;

    if (!user?.id) return null;

    return (
        <>
            <div
                className={`chat-widget-container ${isOpen ? 'open' : ''}`}
                onKeyDown={handleKeyDown}
            >
                {/* ── FAB ────────────────────────────────────────── */}
                <button
                    className={`chat-toggle-button ${!isOpen && totalUnread > 0 ? 'blinking' : ''}`}
                    onClick={() => isOpen ? handleClose() : setIsOpen(true)}
                    aria-label={isOpen ? 'Fechar chat' : 'Abrir chat'}
                    title={isOpen ? 'Fechar chat' : 'Abrir chat'}
                >
                    {isOpen ? <FaTimes /> : <FaCommentDots />}
                    {!isOpen && totalUnread > 0 && (
                        <span className="unread-count" aria-label={`${totalUnread} mensagens não lidas`}>
                            {totalUnread > 99 ? '99+' : totalUnread}
                        </span>
                    )}
                </button>

                <div className="chat-main-window" role="dialog" aria-label="Chat">

                    {/* ── Painel de contatos ─────────────────────── */}
                    <div className={`user-list-panel ${activeChatUserId ? 'hidden-mobile' : ''}`}>
                        <div className="chat-header">
                            <h3>Mensagens</h3>
                            <button className="icon-btn close-btn" onClick={handleClose} title="Fechar">
                                <FaTimes />
                            </button>
                        </div>

                        <div className="search-wrapper">
                            <FaSearch className="search-icon" />
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Buscar contato..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <ul className="user-list" role="listbox">
                            {usersToDisplay.length === 0 && (
                                <li className="no-users-item">
                                    {searchQuery ? 'Nenhum contato encontrado.' : 'Nenhum contato disponível.'}
                                </li>
                            )}
                            {usersToDisplay.map(u => (
                                <li
                                    key={u.userId}
                                    className={[
                                        activeChatUserId === u.userId ? 'active'  : '',
                                        unreadSenders.has(u.userId)   ? 'unread'  : '',
                                        u.isFavorite                  ? 'favorite': ''
                                    ].filter(Boolean).join(' ')}
                                    role="option"
                                    aria-selected={activeChatUserId === u.userId}
                                >
                                    <div
                                        className="contact-area"
                                        onClick={() => openConversation(u.userId)}
                                    >
                                        <UserAvatar user={u} isOnline={u.isOnline} />
                                        <div className="contact-info">
                                            <span className="user-name">{u.name}</span>
                                            {u.isOnline && <span className="online-label">Online</span>}
                                        </div>
                                        {unreadSenders.has(u.userId) && (
                                            <span className="unread-dot" aria-hidden="true" />
                                        )}
                                    </div>
                                    <button
                                        className={`fav-btn ${u.isFavorite ? 'active' : ''}`}
                                        onClick={e => { e.stopPropagation(); toggleFavorite(u.userId); }}
                                        title={u.isFavorite ? 'Remover favorito' : 'Adicionar favorito'}
                                        aria-label={u.isFavorite ? 'Remover favorito' : 'Adicionar favorito'}
                                    >
                                        {u.isFavorite ? <FaStar /> : <FaRegStar />}
                                    </button>
                                </li>
                            ))}
                        </ul>

                        <div className="user-list-footer">
                            <UserAvatar user={user} size="small" />
                            <span className="footer-name">{user.nome}</span>
                            <button
                                className="icon-btn logout-btn"
                                onClick={handleClose}
                                title="Fechar chat"
                                aria-label="Fechar chat"
                            >
                                <FaSignOutAlt />
                            </button>
                        </div>
                    </div>

                    {/* ── Painel de conversa ─────────────────────── */}
                    <div className={`chat-window-panel ${!activeChatUserId ? 'hidden-mobile' : ''}`}>
                        {activeChatUser ? (
                            <>
                                <div className="chat-header">
                                    <button
                                        className="icon-btn back-btn"
                                        onClick={() => setActiveChatUserId(null)}
                                        title="Voltar"
                                        aria-label="Voltar para contatos"
                                    >
                                        <FaArrowLeft />
                                    </button>
                                    <div className="chat-header-user">
                                        <UserAvatar
                                            user={activeChatUser}
                                            isOnline={onlineUsers.some(o => o.userId === activeChatUserId)}
                                        />
                                        <div>
                                            <h3>{activeChatUser.name}</h3>
                                            <span className="header-status">
                                                {onlineUsers.some(o => o.userId === activeChatUserId) ? 'Online' : 'Offline'}
                                            </span>
                                        </div>
                                    </div>
                                    <button className="icon-btn close-btn" onClick={handleClose} title="Fechar">
                                        <FaTimes />
                                    </button>
                                </div>

                                <div className="messages-area" role="log" aria-live="polite">
                                    {(messages[activeChatUserId] || []).map((msg, i) => (
                                        <MessageBubble
                                            key={msg.id || i}
                                            msg={msg}
                                            activeChatUser={activeChatUser}
                                        />
                                    ))}
                                    {isTyping && <TypingIndicator />}
                                    <div ref={messagesEndRef} />
                                </div>

                                <form className="message-form" onSubmit={handleSend}>
                                    <button
                                        type="button"
                                        className="form-icon-btn emoji-btn"
                                        onClick={() => setShowEmojiPicker(p => !p)}
                                        title="Emoji"
                                        aria-label="Abrir seletor de emoji"
                                    >
                                        <FaSmile />
                                    </button>

                                    <button
                                        type="button"
                                        className="form-icon-btn"
                                        onClick={() => setShowLinkModal(true)}
                                        title="Vincular tarefa ou OS"
                                        aria-label="Vincular tarefa ou OS"
                                    >
                                        <FaTasks />
                                    </button>

                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={e => { handleFileUpload(e.target.files[0]); e.target.value = ''; }}
                                        style={{ display: 'none' }}
                                        aria-hidden="true"
                                    />
                                    <button
                                        type="button"
                                        className="form-icon-btn"
                                        onClick={() => fileInputRef.current?.click()}
                                        title="Anexar arquivo"
                                        aria-label="Anexar arquivo"
                                        disabled={uploadingFile}
                                    >
                                        {uploadingFile ? <FaSpinner className="chat-attachment-spin" /> : <FaPaperclip />}
                                    </button>

                                    <input
                                        type="text"
                                        ref={messageInputRef}
                                        className="message-input"
                                        placeholder="Digite uma mensagem..."
                                        value={messageText}
                                        onChange={handleTyping}
                                        autoComplete="off"
                                        aria-label="Campo de mensagem"
                                    />

                                    <button
                                        type="submit"
                                        className="send-btn"
                                        disabled={!messageText.trim()}
                                        title="Enviar mensagem"
                                        aria-label="Enviar mensagem"
                                    >
                                        <FaPaperPlane />
                                    </button>
                                </form>

                                {showEmojiPicker && (
                                    <div className="emoji-picker-wrapper">
                                        <Picker
                                            onEmojiClick={(obj) => setMessageText(p => p + obj.emoji)}
                                            lazyLoadEmojis
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="no-chat-selected">
                                <FaCommentDots />
                                <p>Selecione um contato para iniciar uma conversa.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showLinkModal && (
                <LinkTaskModal
                    onClose={() => setShowLinkModal(false)}
                    onLink={(item) => {
                        sendMessage({
                            senderId:   user.id,
                            receiverId: activeChatUserId,
                            text:       `Vínculo: ${item.titulo}`,
                            file:       { type: 'link', linkData: item }
                        });
                        setShowLinkModal(false);
                    }}
                />
            )}
        </>
    );
}